/**
 * Lightweight date utilities for the calendar views.
 *
 * Design notes:
 * - PlainDate is used for grid math (year / month / day in the *viewer's*
 *   timezone) — wall-clock arithmetic only, no instants.
 * - For instants (event `startsAt`/`endsAt`), we never call `slice(0,10)`
 *   directly. Use `localDateKey(iso, viewerTz)` to bucket events onto the
 *   correct *viewer-local* day, regardless of the event's offset.
 * - We deliberately avoid Temporal — its polyfill weight isn't justified
 *   for the day/week math we do. `Intl.DateTimeFormat` with a `timeZone`
 *   option gives us correct projection.
 */

export interface PlainDate {
  readonly year: number;
  /** 1–12. */
  readonly month: number;
  /** 1–31. */
  readonly day: number;
}

const PAD2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/** Format a PlainDate or Date to `YYYY-MM-DD`. */
export function isoDate(d: PlainDate | Date): string {
  if (d instanceof Date) {
    return `${d.getFullYear()}-${PAD2(d.getMonth() + 1)}-${PAD2(d.getDate())}`;
  }
  return `${d.year}-${PAD2(d.month)}-${PAD2(d.day)}`;
}

/** Parse the date prefix of an ISO string. Tolerates full timestamps.
 *  Note: ignores any TZ offset — for TZ-aware bucketing prefer
 *  `localDateKey(iso, viewerTz)`. */
export function parseISODate(s: string): PlainDate {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return { year: y ?? 1970, month: m ?? 1, day: d ?? 1 };
}

// ---------------------------------------------------------------------------
// Timezone-aware projection
// ---------------------------------------------------------------------------

/** Returns the resolved IANA timezone of the current environment, e.g.
 *  `America/Los_Angeles`. Falls back to UTC if the runtime declines to
 *  resolve (very rare; pre-2017 environments). */
export function defaultViewerTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Detect whether an ISO string is *date-only* (`YYYY-MM-DD`). All-day
 *  events are TZ-neutral by spec — we never reproject them. */
export function isDateOnlyISO(s: string): boolean {
  return s.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

interface TzParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

const tzFormatterCache = new Map<string, Intl.DateTimeFormat>();

function tzFormatter(tz: string): Intl.DateTimeFormat {
  const cached = tzFormatterCache.get(tz);
  if (cached !== undefined) return cached;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  tzFormatterCache.set(tz, fmt);
  return fmt;
}

/** Project an instant onto a target IANA timezone, returning `{year,
 *  month, day, hour, minute}`. Uses `Intl.DateTimeFormat.formatToParts`
 *  which is the only correct way (DST etc.). */
function projectToTz(instant: Date, tz: string): TzParts {
  const parts = tzFormatter(tz).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const p = parts.find((x) => x.type === type);
    return p === undefined ? 0 : Number(p.value);
  };
  let hour = get('hour');
  // `hour: '2-digit', hour12: false` may emit "24" at midnight in some
  // engines; normalise to 0.
  if (hour === 24) hour = 0;
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
  };
}

/**
 * The viewer-local date key (`YYYY-MM-DD`) for an event's start instant.
 * - Date-only ISO strings are returned as-is (all-day; TZ-neutral).
 * - Full ISO timestamps are projected onto `viewerTz`.
 *
 * Note: offset-less ISO strings (e.g. `2026-05-14T08:00`) are *not*
 * NaN — `new Date()` parses them as system-local. Pass values with an
 * explicit offset (`Z` or `±HH:MM`) for unambiguous bucketing.
 */
export function localDateKey(iso: string, viewerTz?: string): string {
  if (isDateOnlyISO(iso)) return iso;
  const tz = viewerTz ?? defaultViewerTz();
  const d = new Date(iso);
  // Last-ditch fallback for malformed input — return the date-prefix
  // best-effort so the caller still has a key to bucket on.
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const p = projectToTz(d, tz);
  return `${p.year}-${PAD2(p.month)}-${PAD2(p.day)}`;
}

/**
 * The viewer-local clock time (`HH:MM`) for an event start instant.
 * Returns `null` for date-only strings (all-day events) and for
 * malformed ISO. Projects onto `viewerTz`; respects the event's offset
 * if present in the ISO string. Offset-less inputs are parsed as
 * system-local (JS default).
 */
export function localTime(iso: string, viewerTz?: string): string | null {
  if (isDateOnlyISO(iso)) return null;
  const tz = viewerTz ?? defaultViewerTz();
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = projectToTz(d, tz);
  return `${PAD2(p.hour)}:${PAD2(p.minute)}`;
}

/** Decimal hours-since-midnight in the viewer TZ. Returns `null` for
 *  date-only / invalid input. */
export function localHourFraction(iso: string, viewerTz?: string): number | null {
  const t = localTime(iso, viewerTz);
  if (t === null) return null;
  const [hh, mm] = t.split(':').map(Number);
  return (hh ?? 0) + (mm ?? 0) / 60;
}

/**
 * Resolve a *viewer-TZ wall-clock* (`{year, month, day, hour, minute}`) to
 * its corresponding UTC instant in milliseconds. Inverse of `projectToTz`.
 *
 * Used by drag-to-create / drag-to-move: the user picks a position on a
 * grid that represents 9:30 in *their* timezone — we have to write back an
 * absolute ISO instant, not a system-local one, so PT users viewing an ET
 * calendar end up with the right `startsAt`.
 *
 * Two-pass iterative refinement converges across DST boundaries; when
 * `tz` is omitted we fall back to system-local Date construction (legacy
 * behaviour for callers that didn't opt in).
 */
export function instantFromZonedWallClock(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  tz?: string,
): number {
  if (tz === undefined) {
    return new Date(year, month - 1, day, hour, minute).getTime();
  }
  const desired = Date.UTC(year, month - 1, day, hour, minute);
  let utc = desired;
  // Pass 1: see how the tz projects our first guess (treating wall as UTC).
  // Pass 2: correct for the offset shift, which suffices unless the wall
  //   sits on a DST overlap (in which case the spec is ambiguous and any
  //   resolution within the overlap hour is acceptable).
  for (let i = 0; i < 2; i++) {
    const p = projectToTz(new Date(utc), tz);
    const projectedUTC = Date.UTC(
      p.year,
      p.month - 1,
      p.day,
      p.hour,
      p.minute,
    );
    const diff = desired - projectedUTC;
    if (diff === 0) break;
    utc += diff;
  }
  return utc;
}

/** Event duration in minutes. Returns null when endsAt is missing or
 *  invalid (callers should assume a default). */
export function eventDurationMinutes(
  startsAt: string,
  endsAt: string | null | undefined,
): number | null {
  if (endsAt === null || endsAt === undefined) return null;
  // Date-only end behaves like end-of-day relative to start-of-day for
  // all-day events — we don't need it for hourly grid math.
  if (isDateOnlyISO(endsAt) || isDateOnlyISO(startsAt)) return null;
  const a = new Date(startsAt).getTime();
  const b = new Date(endsAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  const diff = (b - a) / 60_000;
  return diff > 0 ? diff : null;
}

// ---------------------------------------------------------------------------
// Wall-clock arithmetic (PlainDate)
// ---------------------------------------------------------------------------

export function toDate(d: PlainDate): Date {
  return new Date(d.year, d.month - 1, d.day);
}

export function fromDate(d: Date): PlainDate {
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

export function todayPlain(viewerTz?: string): PlainDate {
  if (viewerTz === undefined) return fromDate(new Date());
  const p = projectToTz(new Date(), viewerTz);
  return { year: p.year, month: p.month, day: p.day };
}

export function todayKey(viewerTz?: string): string {
  return isoDate(todayPlain(viewerTz));
}

export function isSameDay(a: PlainDate, b: PlainDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

export function compareDay(a: PlainDate, b: PlainDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

export function addDays(d: PlainDate, n: number): PlainDate {
  const dt = toDate(d);
  dt.setDate(dt.getDate() + n);
  return fromDate(dt);
}

export function addMonths(d: PlainDate, n: number): PlainDate {
  const dt = new Date(d.year, d.month - 1 + n, 1);
  return fromDate(dt);
}

export function startOfMonth(d: PlainDate): PlainDate {
  return { year: d.year, month: d.month, day: 1 };
}

export function daysInMonth(d: PlainDate): number {
  return new Date(d.year, d.month, 0).getDate();
}

/** 0=Sun, 1=Mon, ..., 6=Sat. */
export function dayOfWeek(d: PlainDate): number {
  return toDate(d).getDay();
}

/** Find the start of the week containing `d`, given a `weekStartsOn`. */
export function startOfWeek(d: PlainDate, weekStartsOn = 0): PlainDate {
  const dow = dayOfWeek(d);
  const offset = (dow - weekStartsOn + 7) % 7;
  return addDays(d, -offset);
}

/** Build a 7-column grid covering the entire month (with leading days
 *  from the previous month and trailing days from the next month so every
 *  row has 7 cells). */
export interface MonthGridCell {
  readonly date: PlainDate;
  readonly outOfMonth: boolean;
}
export function buildMonthGrid(cursor: PlainDate, weekStartsOn = 0): readonly MonthGridCell[] {
  const first = startOfMonth(cursor);
  const total = daysInMonth(cursor);
  const lead = (dayOfWeek(first) - weekStartsOn + 7) % 7;
  const cells: MonthGridCell[] = [];

  for (let i = lead; i > 0; i--) {
    cells.push({ date: addDays(first, -i), outOfMonth: true });
  }
  for (let i = 0; i < total; i++) {
    cells.push({ date: addDays(first, i), outOfMonth: false });
  }
  while (cells.length % 7 !== 0) {
    const last = cells.at(-1);
    if (last === undefined) break;
    cells.push({ date: addDays(last.date, 1), outOfMonth: true });
  }
  return cells;
}

/** Build the 7 days of the week containing `d`. */
export function buildWeek(d: PlainDate, weekStartsOn = 0): readonly PlainDate[] {
  const start = startOfWeek(d, weekStartsOn);
  const out: PlainDate[] = [];
  for (let i = 0; i < 7; i++) out.push(addDays(start, i));
  return out;
}

// ---------------------------------------------------------------------------
// Locale / formatting
// ---------------------------------------------------------------------------

export function formatMonth(d: PlainDate, locale?: string): string {
  return toDate(d).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

export function formatWeekday(d: PlainDate, locale?: string, length: 'long' | 'short' | 'narrow' = 'short'): string {
  return toDate(d).toLocaleDateString(locale, { weekday: length });
}

export function formatDayLong(d: PlainDate, locale?: string): string {
  return toDate(d).toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export function formatDayShort(d: PlainDate, locale?: string): string {
  return toDate(d).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

export function weekRangeLabel(start: PlainDate, end: PlainDate, locale?: string): string {
  const s = toDate(start).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const e = toDate(end).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  return `${s} – ${e}, ${start.year}`;
}

/** Ordered weekday header labels starting at `weekStartsOn`. */
export function weekdayHeaders(
  weekStartsOn: number,
  locale: string | undefined,
  length: 'long' | 'short' | 'narrow' = 'short',
): readonly string[] {
  const out: string[] = [];
  // Anchor on a known Sunday (1970-01-04) and walk forward.
  const sunday = new Date(Date.UTC(1970, 0, 4));
  for (let i = 0; i < 7; i++) {
    const d = new Date(sunday);
    d.setUTCDate(sunday.getUTCDate() + ((i + weekStartsOn) % 7));
    out.push(d.toLocaleDateString(locale, { weekday: length, timeZone: 'UTC' }));
  }
  return out;
}

/** Human-relative date label like "Today", "Tomorrow", "In 3 days". */
export function relativeDayLabel(target: PlainDate, today: PlainDate, locale?: string): string {
  const diff = compareDay(target, today);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1 && diff < 7) return `In ${diff} days`;
  if (diff < -1 && diff > -7) return `${-diff} days ago`;
  return formatDayShort(target, locale);
}

/** Format a timeline gutter hour through Intl so AM/PM markers follow
 *  the active locale instead of being hardcoded in English. */
export function formatHourLabel(hour: number, locale?: string): string {
  return new Date(Date.UTC(1970, 0, 1, hour, 0)).toLocaleTimeString(locale, {
    hour: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Format an `HH:MM` clock string for scheduling cards.
 * Shared by SlotCard / GroupEventCard / RecurringCard / BookingSheet so
 * they don't each ship their own formatter.
 */
export function formatClockTime(time: string, locale?: string): string {
  const [h, m] = time.split(':').map(Number);
  const hour = h ?? 0;
  const minute = m ?? 0;
  if (locale !== undefined) {
    return new Date(Date.UTC(1970, 0, 1, hour, minute)).toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: minute === 0 ? undefined : '2-digit',
      timeZone: 'UTC',
    });
  }
  const period = hour >= 12 ? 'pm' : 'am';
  const hh = hour % 12 || 12;
  return minute === 0 ? `${hh}${period}` : `${hh}:${PAD2(minute)}${period}`;
}
